import { Component, ElementRef, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TownshipPublicSettings } from '../interfaces';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { environment } from 'src/environments/environment';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { doc, getDoc } from 'firebase/firestore';
import { getEnvName, getTownshipImage, updateTheme } from '../globals';
import { db, functions } from '../app.component';
import { httpsCallable } from 'firebase/functions';
import { Subscription } from 'rxjs';
import { MatCard, MatCardContent, MatCardActions } from '@angular/material/card';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PublicFunctionsService } from '../services/public-functions.service';

@Component({
  selector: 'app-saldo-checker',
  templateUrl: './saldo-checker.component.html',
  styleUrls: ['./saldo-checker.component.scss'],
  imports: [
    MatCard,
    MatCardContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatCardActions,
    MatButton,
    MatProgressSpinner,
    TranslatePipe,
  ],
})
export class SaldoCheckerComponent implements OnInit, OnDestroy {
  readonly db = db;
  readonly functions = functions;
  env = environment;
  awaitingResponse = signal(false);
  loaded = signal(false);
  townshipId: string;
  township: TownshipPublicSettings;
  saldoCheckerForm: UntypedFormGroup;

  environmentObj: any;
  error: string;
  headerImg: string;

  resultString: string;
  resultAmount?: number;
  resultDate: string;
  showValue: boolean = false;
  showRemaining: boolean = false;
  showDate: boolean = false;

  originalValue: string;
  value: string;

  townshipImage: any;

  @ViewChild('voucherInput', { static: false }) voucherInput: ElementRef;
  private readonly subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private translate: TranslateService,
    private publicFunctionsService: PublicFunctionsService,
  ) {}

  ngOnInit(): void {
    this.saldoCheckerForm = this.fb.group({
      voucher: [, Validators.required],
    });
    this.translate.setDefaultLang(environment.language);
    this.translate.use(environment.language);
    this.subscriptions.add(
      this.route.params.subscribe(async (params) => {
        this.townshipId = params.townshipId;
        this.township = (
          await getDoc(doc(this.db, `township/${this.townshipId}/settings/public`))
        ).data() as TownshipPublicSettings;

        this.environmentObj = getEnvName(this.township.voucherPrefix);
        this.townshipImage = getTownshipImage(this.township);
        updateTheme({
          primary: this.township.primaryColor,
          secondary: this.township.accentColor,
        });
        this.loaded.set(true);
      }),
    );
  }

  getError(name) {
    const field = this.saldoCheckerForm.get(name);
    if (field.touched || !field.pristine) {
      let error;

      if (field.hasError('required')) {
        error = 'required';
      }
      if (field.hasError('unknown_voucher')) {
        error = 'unknown_voucher';
      }
      return this.translate.instant(`errors.${error}`);
    }
  }

  async reset() {
    this.value = null;
    this.originalValue = null;
    this.resultString = null;
    this.showValue = false;
    this.showRemaining = false;
    this.showDate = false;
    this.saldoCheckerForm.reset();
    setTimeout(() => {
      this.voucherInput.nativeElement.click();
    });
  }

  async checkSaldo() {
    this.resultDate = null;
    this.resultString = null;
    this.awaitingResponse.set(true);
    try {
      const token = await this.publicFunctionsService.getRecaptchaToken('check_saldo');
      if (this.saldoCheckerForm.valid && token) {
        const callable = httpsCallable(this.functions, 'httpCheckSaldo');
        const result = await callable({
          voucherNumber: this.saldoCheckerForm.value.voucher.toUpperCase(),
          townshipId: this.townshipId,
          recaptchaToken: token,
        });
        if (result?.data) {
          switch (result.data['type']) {
            case 'claimed':
            case 'partiallyClaimed':
            case 'paid':
              this.showValue = true;
              this.originalValue = result.data['originalValue'];
              this.value = '0';

              if (
                result.data['type'] === 'partiallyClaimed' &&
                result.data['originalValue'] - (result.data['value'] ?? 0) > 0
              ) {
                this.value = (
                  result.data['originalValue'] - (result.data['value'] ?? 0)
                ).toString();
                this.showRemaining = true;
                this.showDate = true;
              } else {
                this.resultString = this.getTranslation('claimed');
              }

              if (result.data['validUntilDate'] && result.data['validUntilDate'] !== 'onbekend') {
                this.resultDate = this.getTranslation('validUntilDate', [
                  result.data['validUntilDate'],
                ]);
              } else {
                this.resultDate = this.getTranslation('noUntilValidDate');
              }
              break;
            case 'expired':
              this.showDate = true;
              this.resultString = this.getTranslation('expired');
              if (result.data['validUntilDate'] && result.data['validUntilDate'] !== 'onbekend') {
                this.resultDate = this.getTranslation('validUntilDate', [
                  result.data['validUntilDate'],
                ]);
              } else {
                this.resultDate = this.getTranslation('noUntilValidDate');
              }
              break;
            case 'activated':
              this.showValue = true;
              this.showRemaining = true;
              this.showDate = true;
              this.originalValue = result.data['originalValue']
                ? result.data['originalValue']
                : result.data['value'];
              this.value = result.data['value'];
              if (result.data['validUntilDate'] && result.data['validUntilDate'] !== 'onbekend') {
                this.resultDate = this.getTranslation('validUntilDate', [
                  result.data['validUntilDate'],
                ]);
              } else {
                this.resultDate = this.getTranslation('noUntilValidDate');
              }
              break;
            case 'unknown_voucher':
              // this.resultString = this.getTranslation(result.data['type']);
              this.saldoCheckerForm.controls.voucher.setErrors({
                unknown_voucher: true,
              });
              break;
            case 'not_used':
              this.resultString = this.getTranslation(result.data['type']);
              break;
            default:
              this.resultString = this.getTranslation('notAvailable');
              break;
          }

          this.value = this.value ? Number.parseFloat(this.value).toFixed(2) : null;
          this.originalValue = Number.parseFloat(this.originalValue).toFixed(2);
          if (!this.value) {
            this.voucherInput.nativeElement.click();
          }
        }
      }
    } finally {
      this.awaitingResponse.set(false);
    }
  }

  getTranslation(objName, values?): string {
    let newObj = {};
    if (values) {
      if (values.length === 2) {
        newObj = {
          value: values[0],
          amount: values[1],
        };
      } else if (values.length === 1) {
        newObj = {
          value: values[0],
        };
      }
    }
    const message = this.translate.instant(`saldo-checker.messages.${objName}`, newObj) as string;
    return message;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
